import { CUSTOMER_KINDS, type CustomerKind } from "@erp/shared";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Building2, Plus, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { useCallback } from "react";
import { z } from "zod";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { DataList, type Column } from "~/components/ui/data-list";
import { Page, PageHeader, Panel, SearchInput, Segmented, Toolbar } from "~/components/ui/page";
import { EmptyState, ErrorState } from "~/components/ui/states";
import { CustomerFormSheet } from "~/features/customers/customer-form";
import { KIND_TONE } from "~/features/customers/types";
import { api, unwrap } from "~/lib/api";
import { errorMessageKey } from "~/lib/errors";
import { listQuery } from "~/lib/forms";
import { useFormat, useT } from "~/lib/i18n";
import { useCan } from "~/lib/session";
import { useInfiniteList } from "~/lib/use-list";
import { HelpTip } from "~/components/ui/help-tip";

const search = z.object({
  q: z.string().optional(),
  kind: z.enum(CUSTOMER_KINDS).optional(),
  archived: z.enum(["exclude", "only"]).optional(),
  sort: z.enum(["displayName", "createdAt", "kind", "origin"]).optional(),
  dir: z.enum(["asc", "desc"]).optional(),
  new: z.boolean().optional(),
});

export const Route = createFileRoute("/_app/customers/")({ validateSearch: search, component: Customers });

type Row = Awaited<ReturnType<typeof fetchCustomers>>["items"][number];

function fetchCustomers(params: z.infer<typeof search> & { offset: number; limit: number }) {
  const { new: _new, ...query } = params;
  // Newest customers first unless a column says otherwise.
  const sort = query.sort ? { sort: query.sort, dir: query.dir ?? "asc" } : { sort: "createdAt" as const, dir: "desc" as const };
  return unwrap(api.customers.get({ query: listQuery({ ...query, ...sort }) }));
}

function Customers() {
  const t = useT();
  const format = useFormat();
  const can = useCan();
  const params = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const { new: creating, ...filters } = params;

  const list = useInfiniteList("customers", filters, fetchCustomers);
  const setSearch = useCallback(
    (patch: Partial<z.infer<typeof search>>) => void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true }),
    [navigate],
  );

  const columns: Column<Row>[] = [
    {
      id: "name",
      header: t("common.fields.name"),
      sortKey: "displayName",
      cell: (row) => <span className="font-medium text-fg">{row.displayName}</span>,
    },
    {
      id: "kind",
      header: t("customers.fields.kind"),
      sortKey: "kind",
      cell: (row) => <Badge tone={KIND_TONE[row.kind]}>{t(`customers.kinds.${row.kind}`)}</Badge>,
    },
    {
      id: "origin",
      header: t("customers.origin.label"),
      sortKey: "origin",
      cell: (row) => <Badge tone={row.origin === "shopify" ? "info" : "neutral"}>{t(`customers.origin.${row.origin}`)}</Badge>,
      minWidth: "lg",
    },
    { id: "email", header: t("common.fields.email"), cell: (row) => row.email ?? "", className: "text-fg-2", minWidth: "lg" },
    {
      id: "phone",
      header: t("common.fields.phone"),
      cell: (row) => row.phone ?? "",
      className: "text-fg-2 tabular",
      minWidth: "xl",
    },
    { id: "city", header: t("common.fields.city"), cell: (row) => row.city ?? "", className: "text-fg-2" },
    {
      id: "createdAt",
      header: t("common.fields.createdAt"),
      sortKey: "createdAt",
      align: "end",
      cell: (row) => format.date(row.createdAt, "short"),
      className: "text-fg-2",
      minWidth: "lg",
    },
  ];

  const filtered = Boolean(filters.q || filters.kind || filters.archived);
  const queryClient = useQueryClient();
  const importShopify = useMutation({
    mutationFn: () => unwrap(api.customers.import.shopify.post()),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ["customers"] });
      toast.success(t("customers.importShopifyDone", { created: result.created, linked: result.linked }));
    },
    onError: (error) => toast.error(t(errorMessageKey(error))),
  });

  return (
    <Page>
      <PageHeader
        title={t("customers.title")}
        meta={list.isLoading ? null : t("common.table.rows", { count: list.total })}
        actions={
          <>
            {can("imports:run") ? (
              <span className="inline-flex items-center gap-0.5">
                <Button onClick={() => importShopify.mutate()} loading={importShopify.isPending}>
                  {importShopify.isPending ? null : <RefreshCw />}
                  {t("customers.importShopify")}
                </Button>
                <HelpTip id="customer.importShopify" />
              </span>
            ) : null}
            {can("customers:write") ? (
              <Button variant="primary" onClick={() => setSearch({ new: true })}>
                <Plus />
                {t("customers.new")}
              </Button>
            ) : null}
          </>
        }
      />
      <Panel>
        <Toolbar>
          <SearchInput
            value={filters.q ?? ""}
            onChange={(q) => setSearch({ q: q || undefined })}
            placeholder={t("customers.searchPlaceholder")}
          />
          <Segmented
            label={t("customers.fields.kind")}
            value={filters.archived === "only" ? "archived" : (filters.kind ?? "all")}
            onChange={(value) =>
              setSearch(
                value === "archived"
                  ? { archived: "only", kind: undefined }
                  : { archived: undefined, kind: value === "all" ? undefined : (value as CustomerKind) },
              )
            }
            options={[
              { value: "all", label: t("customers.all") },
              ...CUSTOMER_KINDS.map((kind) => ({ value: kind, label: t(`customers.kinds.${kind}`) })),
              { value: "archived", label: t("customers.showArchived") },
            ]}
          />
        </Toolbar>
        {list.isError ? (
          <ErrorState error={list.error} onRetry={() => void list.refetch()} />
        ) : (
          <DataList
            rows={list.rows}
            rowKey={(row) => row.id}
            columns={columns}
            card={(row) => (
              <>
                <div className="flex items-center gap-2">
                  <span className="truncate font-medium">{row.displayName}</span>
                  <Badge tone={KIND_TONE[row.kind]}>{t(`customers.kinds.${row.kind}`)}</Badge>
                </div>
                <p className="mt-0.5 truncate text-sm text-fg-2">
                  {[row.city, row.email ?? row.phone].filter(Boolean).join(" — ")}
                </p>
              </>
            )}
            onOpen={(row) => void navigate({ to: "/customers/$id", params: { id: row.id } })}
            sort={filters.sort ? { key: filters.sort, dir: filters.dir ?? "asc" } : { key: "createdAt", dir: "desc" }}
            onSortChange={(s) => setSearch({ sort: s.key as z.infer<typeof search>["sort"], dir: s.dir })}
            loading={list.isLoading}
            hasMore={list.hasMore}
            loadingMore={list.loadingMore}
            onLoadMore={list.loadMore}
            isDimmed={(row) => Boolean(row.archivedAt)}
            empty={
              filtered ? (
                <EmptyState title={t("common.states.noResults")} body={t("common.states.noResultsHint")} />
              ) : (
                <EmptyState
                  icon={<Building2 />}
                  title={t("customers.emptyTitle")}
                  body={t("customers.emptyBody")}
                  action={
                    can("customers:write") ? (
                      <Button variant="primary" onClick={() => setSearch({ new: true })}>
                        <Plus />
                        {t("customers.new")}
                      </Button>
                    ) : null
                  }
                />
              )
            }
          />
        )}
      </Panel>
      <CustomerFormSheet
        open={Boolean(creating)}
        onOpenChange={(open) => setSearch({ new: open || undefined })}
        onSaved={(customer) => void navigate({ to: "/customers/$id", params: { id: customer.id } })}
      />
    </Page>
  );
}
